import { useMemo } from 'react';
import { checkMinutes, programById, programDay } from '@erectus/shared';
import type { BreakLogEntry, Program, ProgramDay, ProgramSnack, ProgramWeek, SnackLogEntry } from '@erectus/shared';
import { useAuth } from '@/stores/auth.js';
import { localDayKey, logBreak, logSnack } from '@/lib/repo.js';
import { markStale, useWarm } from '@/lib/warmCache.js';
import { loaders, warmKeys } from '@/lib/warmup.js';
import { track } from '@/lib/analytics.js';

export interface ProgramToday {
  program: Program;
  week: ProgramWeek;
  day: ProgramDay;
  /** Today's snacks, in order. */
  snacks: ProgramSnack[];
  checkMin: number;
  finished: boolean;
}

/** The enrolled program and where today falls in it; null without one. */
export function useProgramToday(now = Date.now()): ProgramToday | null {
  const { userDoc } = useAuth();
  const enrollment = userDoc?.program ?? null;
  return useMemo(() => {
    if (!enrollment || enrollment.endedAt) return null;
    const program = programById(enrollment.programId);
    const at = programDay(enrollment, now);
    if (!program || !at) return null;
    const week = program.plan[at.week - 1];
    const day = week?.days.find((d) => d.day === at.day);
    if (!week || !day) return null;
    const snacks = day.snackIds
      .map((id) => week.snacks.find((s) => s.id === id))
      .filter((s): s is ProgramSnack => Boolean(s));
    return { program, week, day, snacks, checkMin: checkMinutes(program, at.week, at.day), finished: at.finished };
    // Recomputed per render is cheap; `now` changes every render by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollment?.programId, enrollment?.startedAt, enrollment?.endedAt, Math.floor(now / 60_000)]);
}

/** Breaks and snacks logged, with today's counts and the actions that log more. */
export function useHabits() {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const [breaksState, setBreaks] = useWarm<BreakLogEntry[]>(
    uid ? warmKeys.breakLog(uid) : null,
    () => loaders.breakLog(uid!),
    'Could not load your breaks.',
  );
  const [snacksState, setSnacks] = useWarm<SnackLogEntry[]>(
    uid ? warmKeys.snackLog(uid) : null,
    () => loaders.snackLog(uid!),
    'Could not load your exercise snacks.',
  );
  const breaks = breaksState.status === 'ready' ? breaksState.data : [];
  const snacks = snacksState.status === 'ready' ? snacksState.data : [];
  const today = localDayKey(Date.now());
  const breaksToday = breaks.filter((b) => localDayKey(b.at) === today);
  const snacksToday = snacks.filter((s) => localDayKey(s.at) === today);

  const addBreak = async (source: BreakLogEntry['source']): Promise<void> => {
    if (!uid) return;
    track('break_logged', { source });
    const optimistic: BreakLogEntry = { at: Date.now(), source };
    setBreaks([optimistic, ...breaks]);
    const saved = await logBreak(uid, source).catch(() => null);
    if (!saved) setBreaks(breaks);
  };

  const finishSnack = async (snack: ProgramSnack, programId: string | null): Promise<void> => {
    if (!uid || snacksToday.some((s) => s.snackId === snack.id)) return;
    track('snack_done', { snack_id: snack.id, in_program: programId !== null });
    const entry: SnackLogEntry = { at: Date.now(), snackId: snack.id, programId, exerciseIds: snack.exerciseIds };
    setSnacks([entry, ...snacks]);
    const saved = await logSnack(uid, entry).catch(() => null);
    if (!saved) setSnacks(snacks);
    // The moves were logged one by one too; the library and calendar re-read them.
    markStale(warmKeys.exerciseLog(uid));
  };

  return {
    ready: breaksState.status === 'ready' && snacksState.status === 'ready',
    breaks,
    snacks,
    breaksToday,
    snacksToday,
    addBreak,
    finishSnack,
  };
}
